import React, { useState } from 'react';
import { PlayIcon, VideoIcon, Volume2Icon, MaximizeIcon } from 'lucide-react';
interface VideoReplayProps {
  /** URL de Vimeo, Wistia, YouTube o archivo propio. */
  url?: string;
  thumbnailUrl?: string;
  caption?: string;
  title?: string;
  /** Texto superpuesto sobre la miniatura, editable desde el constructor. */
  overlayTitle?: React.ReactNode;
  className?: string;
  /** Ancho máximo del reproductor. */
  maxWidthClass?: string;
}
function toEmbedUrl(url: string): string | null {
  const youtube = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}?autoplay=1`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
  return null;
}
function isFileUrl(url: string): boolean {
  return /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url);
}

/**
 * Reproductor de vídeo reutilizable en proporción 16:9, protagonista del hero.
 * Se usa en la landing de AVA y en cualquier landing con bloque de vídeo.
 */
export function VideoReplay({
  url,
  thumbnailUrl,
  caption,
  title = 'Vídeo replay',
  overlayTitle,
  className = '',
  maxWidthClass = 'max-w-5xl'
}: VideoReplayProps) {
  const [playing, setPlaying] = useState(false);
  const embedUrl = url ? toEmbedUrl(url) : null;
  const hasSource = Boolean(url);
  return <figure className={`w-full ${maxWidthClass} mx-auto ${className}`} data-id="element-617" data-mp="2mne81pet7wu4">
      <div className="ava-video-shell relative w-full overflow-hidden rounded-2xl bg-navy ring-1 ring-black/5 sm:rounded-3xl" data-id="element-618" data-mp="2rnaedvazc0cu">
        <div className="relative aspect-video w-full" data-id="element-619" data-mp="2vq70hvzqaelj">
          {playing && hasSource ? embedUrl ? <iframe src={embedUrl} title={title} className="absolute inset-0 h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture" allowFullScreen data-id="element-620" data-mp="30ogsjxq5oz2x" /> : isFileUrl(url as string) ? <video src={url} controls autoPlay className="absolute inset-0 h-full w-full object-cover" data-id="element-621" data-mp="16az3u7e0lw6b" /> : <iframe src={url} title={title} className="absolute inset-0 h-full w-full" allowFullScreen data-id="element-622" data-mp="29m4x2cut5tk4" /> : <button type="button" onClick={() => hasSource && setPlaying(true)} aria-label={hasSource ? 'Reproducir el vídeo' : 'Vídeo pendiente de configurar'} className="group absolute inset-0 h-full w-full focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/40" data-id="element-623" data-mp="24n1z1cd8wnln">
              {thumbnailUrl ? <img src={thumbnailUrl} alt="" className="h-full w-full object-cover" data-id="element-624" data-mp="2sixp65s8oocv" /> : <span className="flex h-full w-full flex-col items-center justify-center gap-2 bg-surface text-muted-foreground" data-id="element-625" data-mp="2nl4lpkibm8ga">
                  <VideoIcon className="h-8 w-8" aria-hidden="true" data-id="element-626" data-mp="31azlpsz6tkhv" />
                  <span className="text-sm" data-id="element-627" data-mp="2xa5y70pnq7pi">Miniatura pendiente</span>
                </span>}

              <span className="absolute inset-0 bg-gradient-to-t from-black-premium/35 via-transparent to-transparent" data-id="element-628" data-mp="3ba7978zmyvip" />

              {overlayTitle && <span className="absolute inset-x-0 top-[16%] whitespace-pre-line px-6 text-center text-xl font-bold leading-tight text-white drop-shadow-[0_2px_14px_rgba(0,0,0,0.45)] sm:text-3xl" data-id="element-629" data-mp="36cowr8mety5y">
                  {overlayTitle}
                </span>}

              {/* Botón de reproducción */}
              <span className="absolute left-1/2 top-1/2 flex h-[72px] w-[72px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.45)] transition-transform duration-300 ease-out group-hover:scale-105 sm:h-24 sm:w-24" data-id="element-630" data-mp="3fy6pneq99i79">
                <span className="absolute inset-0 rounded-full ring-8 ring-white/25" data-id="element-631" data-mp="3byfyvb0qb22q" />
                <PlayIcon className="ml-1 h-7 w-7 text-primary sm:h-9 sm:w-9" fill="currentColor" aria-hidden="true" data-id="element-632" data-mp="2x99uz9qy4noi" />
              </span>

              {/* Barra de controles decorativa */}
              <span className="absolute inset-x-0 bottom-0 flex items-center gap-3 px-4 py-3 text-white/90 sm:px-6 sm:py-4" data-id="element-633" data-mp="2o7k2e0lbvip5">
                <PlayIcon className="h-4 w-4 flex-none" fill="currentColor" aria-hidden="true" data-id="element-634" data-mp="2s841h9pgowdx" />
                <span className="text-[11px] tabular-nums sm:text-xs" data-id="element-635" data-mp="2l4w40jptl48f">0:00 / 4:37</span>
                <span className="h-1 flex-1 overflow-hidden rounded-full bg-white/30" data-id="element-636" data-mp="2g85opyluaco2">
                  <span className="block h-full w-[4%] rounded-full bg-white" data-id="element-637" data-mp="2bfmb51n5jhj9" />
                </span>
                <Volume2Icon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-638" data-mp="26i2w3zimr0q9" />
                <MaximizeIcon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-639" data-mp="22fg33va839rh" />
              </span>

              {!hasSource && <span className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-muted-foreground" data-id="element-640" data-mp="1xgd4asku5zry">
                  Añade la URL del vídeo desde el editor
                </span>}
            </button>}
        </div>
      </div>

      {caption && <figcaption className="mx-auto mt-8 max-w-2xl text-center text-base text-muted-foreground sm:text-lg" data-id="element-641" data-mp="3mw2sj0i9o88r">
          {caption}
        </figcaption>}
    </figure>;
}